import { FC, useState } from 'react';
import { Box, Drawer, Flex, LinearProgress } from '@mafc/trolly';
import { useTranslation } from 'react-i18next';
import { COLORS } from '@mafc/common';
import CartListItem from '../CartListItem';
import { isNonEmptyObject } from 'utils/helpers.util';
import { CartListItemVariant } from '../CartListItem/CartListItem.style';
import { InlineNotification } from 'eds/Notifications';
import { ENotificationVariant } from 'eds/Notifications/types';
import { ISubstitutesShipment } from 'pages/NewCart/ISubstitutes.types';
import useOOSChooseReplacement from 'pages/NewCart/hooks/useOOSChooseReplacement';

import * as S from './OOSChooseSubstitutesDrawer.style';

export const Title: FC<{
  currentOOSProductCount: number;
  totalOOSSelectedProductCount: number;
}> = ({ currentOOSProductCount, totalOOSSelectedProductCount }) => {
  const { t } = useTranslation();

  return (
    <Box>
      <Box fontWeight={[5, 6]} fontSize={[3, 6]} textAlign="center">
        {t('oosSubstitution.heading')}
      </Box>

      <Flex justifyContent="center">
        <S.ItemCounterWrapper>
          {t('oosSubstitution.item')}{' '}
          <S.ItemCounter>
            {currentOOSProductCount}/{totalOOSSelectedProductCount}
          </S.ItemCounter>
        </S.ItemCounterWrapper>
      </Flex>
    </Box>
  );
};

export const OOSChooseSubstitutesDrawer: React.FC<{
  isOpen: boolean;
  shipmentType: string;
  substitutesShipment?: ISubstitutesShipment;
  close: () => void;
}> = ({ isOpen, shipmentType, substitutesShipment, close }) => {
  const { t } = useTranslation();

  const [selectedSubstituteProductId, setSelectedSubstituteProductId] = useState('');

  const {
    originalProduct,
    productSubstitutes,
    totalOOSSelectedProductCount,
    currentOOSProductCount,
    errorSubstituteReplacement,
    loadingSubstituteReplacement,
    onSkip,
    onContinue,
    onSubstituteQtyChange,
  } = useOOSChooseReplacement({
    substitutesShipment,
  });

  const onSubstituteItemChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSubstituteQtyChange(0);
    setSelectedSubstituteProductId(event.target.value);
  };

  return (
    <Drawer
      onClose={close}
      isOpen={isOpen}
      width={[null, null, '480px']}
      anchor={['bottom', null, 'right']}
      contentStyles={{
        p: [0],
        pl: 5,
        pr: 5,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Montserrat',
        color: COLORS.BLACK,
      }}
      containerStyles={{
        borderTopLeftRadius: [4, null, null, 0],
        borderTopRightRadius: [4, null, null, 0],
        height: ['85%', null, '100%'],
      }}
      preventCloseOnClickOutside={true}
      isTitleBorder={false}
      variant="float"
      title={
        <Title
          currentOOSProductCount={currentOOSProductCount}
          totalOOSSelectedProductCount={totalOOSSelectedProductCount}
        />
      }
    >
      {isNonEmptyObject(originalProduct) && (
        <CartListItem
          entry={originalProduct}
          shipmentType={shipmentType}
          isLoading={false}
          isOOSSubstitutionDrawer
          variant={CartListItemVariant.DrawerHeader}
        />
      )}

      <S.SubTitle>{t('oosSubstitution.subTitle')}</S.SubTitle>
      <Box mb={errorSubstituteReplacement ? 180 : 90}>
        {productSubstitutes.map(entry => (
          <S.RadioLabel data-testid={`substitute-${entry.entryNumber}`} key={entry.entryNumber}>
            <S.RadioButton
              type="radio"
              name="substituteItem"
              value={entry.entryNumber}
              onChange={onSubstituteItemChangeHandler}
            />
            <CartListItem
              entry={entry}
              shipmentType={shipmentType}
              isOOSSubstitutionDrawer
              isLoading={false}
              showQtySelector={String(entry.entryNumber) === selectedSubstituteProductId}
              variant={CartListItemVariant.DrawerItem}
              onSubstituteQtyChange={onSubstituteQtyChange}
            />
          </S.RadioLabel>
        ))}
      </Box>

      <S.Footer>
        {loadingSubstituteReplacement && (
          <LinearProgress position="bottom" data-testid="substitute-progress" />
        )}

        <S.ButtonRow>
          <S.SkipButton variant="naked" onClick={onSkip}>
            {t('oosSubstitution.skip')}
          </S.SkipButton>

          <S.ContinueButton
            variant="secondary"
            size="CUSTOM"
            preventTextTransform
            disabled={!selectedSubstituteProductId}
            onClick={onContinue.bind(null, selectedSubstituteProductId)}
          >
            {t('oosSubstitution.continue')}
          </S.ContinueButton>
        </S.ButtonRow>

        {errorSubstituteReplacement && (
          <S.ErrorWrapper>
            <InlineNotification
              variant={ENotificationVariant.ERROR}
              description={t('oosSubstitution.replacementError')}
              showBorder
            />
          </S.ErrorWrapper>
        )}
      </S.Footer>
    </Drawer>
  );
};
